import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from '../hooks/use-toast';
import { CheckCircle, Crown, Zap, Download, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManager = () => {
  const { user, token, refreshUser } = useAuth();
  const [subscriptionTiers, setSubscriptionTiers] = useState({});
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    fetchSubscriptionTiers();
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await fetch(`${API}/subscription/tiers`);
      const data = await response.json();
      setSubscriptionTiers(data.tiers);
    } catch (error) {
      console.error('Error fetching subscription tiers:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handleUpgrade = async (tier) => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tier: tier,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to create checkout session",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to start checkout process",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Subscription canceled successfully"
        });
        await refreshUser();
        await fetchSubscriptionStatus();
      } else {
        const data = await response.json();
        toast({
          title: "Error",
          description: data.detail || "Failed to cancel subscription",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    if (!token) return;
    
    setLoading(true);
    try {
      const endpoint = type === 'receipts' ? '/export/receipts' : '/export/items';
      const response = await fetch(`${API}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create and download CSV file
        const blob = new Blob([data.content], { type: data.content_type });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: "Success",
          description: `${type === 'receipts' ? 'Receipts' : 'Items'} exported successfully`
        });
      } else {
        const data = await response.json();
        toast({
          title: "Error",
          description: data.detail || "Export failed",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Error",
        description: "Export failed",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'free': return <CheckCircle className="h-5 w-5" />;
      case 'basic': return <Zap className="h-5 w-5" />;
      case 'premium': return <Crown className="h-5 w-5" />;
      default: return <CheckCircle className="h-5 w-5" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'free': return 'bg-gray-100 text-gray-800';
      case 'basic': return 'bg-blue-100 text-blue-800';
      case 'premium': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user || !subscriptionStatus) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription information...</p>
        </div>
      </div>
    );
  }

  const currentTier = subscriptionStatus.subscription_tier;
  const uploadsUsed = subscriptionStatus.uploads_this_month;
  const uploadsRemaining = subscriptionStatus.uploads_remaining;
  const uploadLimit = subscriptionStatus.upload_limit;
  const usagePercentage = uploadLimit === -1 ? 0 : (uploadsUsed / uploadLimit) * 100;

  return (
    <div className="space-y-6">
      {/* Current Subscription Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getTierIcon(currentTier)}
            Current Subscription: {subscriptionTiers[currentTier]?.name || currentTier}
          </CardTitle>
          <CardDescription>
            Manage your subscription and view usage statistics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge className={getTierColor(currentTier)}>
              {subscriptionTiers[currentTier]?.name || currentTier}
            </Badge>
            <span className="text-sm text-gray-600">
              Status: {subscriptionStatus.subscription_status}
            </span>
          </div>

          {/* Upload Usage */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Monthly Uploads</span>
              <span>
                {uploadsUsed} / {uploadLimit === -1 ? '∞' : uploadLimit}
              </span>
            </div>
            {uploadLimit !== -1 && (
              <Progress value={usagePercentage} className="h-2" />
            )}
            {uploadsRemaining !== -1 && uploadsRemaining <= 5 && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>Only {uploadsRemaining} uploads remaining this month</span>
              </div>
            )}
          </div>

          {/* Export Options */}
          <div className="flex gap-2 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('receipts')}
              disabled={!subscriptionStatus.can_export_receipts || loading}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Receipts
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('items')}
              disabled={!subscriptionStatus.can_export_items || loading}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Items
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Tiers */}
      <div className="grid md:grid-cols-3 gap-6">
        {Object.entries(subscriptionTiers).map(([tierKey, tier]) => (
          <Card key={tierKey} className={`relative ${currentTier === tierKey ? 'ring-2 ring-blue-500' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  {getTierIcon(tierKey)}
                  {tier.name}
                </span>
                {currentTier === tierKey && (
                  <Badge variant="secondary">Current</Badge>
                )}
              </CardTitle>
              <CardDescription>
                <span className="text-2xl font-bold">${tier.price}</span>
                {tier.price > 0 && <span className="text-sm">/month</span>}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {currentTier !== tierKey && tierKey !== 'free' && (
                <Button
                  onClick={() => handleUpgrade(tierKey)}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Processing...' : `Upgrade to ${tier.name}`}
                </Button>
              )}

              {currentTier === tierKey && tierKey !== 'free' && (
                <Button
                  variant="outline"
                  onClick={handleCancelSubscription}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Processing...' : 'Cancel Subscription'}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionManager;